/**
 * Layout pour l'espace salarié
 * Sidebar dark avec accent vert — identité visuelle espace salarié
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  User,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '@features/auth';
import { espaceSalarieApi, ProfilEmploye } from '../../Modules/espace-salarie/api/espaceSalarieApi';

interface NavItem {
  path: string;
  icon: React.ElementType;
  label: string;
  exact?: boolean;
}

const navItems: NavItem[] = [
  { path: '/espace-salarie', icon: FileText, label: 'Mes bulletins', exact: true },
  { path: '/espace-salarie/mon-profil', icon: User, label: 'Mon profil', exact: true },
];

export function EspaceSalarieLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profil, setProfil] = useState<ProfilEmploye | null>(null);

  useEffect(() => {
    espaceSalarieApi.obtenirMonProfil()
      .then(p => setProfil(p))
      .catch(() => setProfil(null));
  }, []);

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const nomEmploye = profil ? `${profil.nom} ${profil.prenoms}` : `${user?.prenom ?? ''} ${user?.nom ?? ''}`.trim();

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-bg transform transition-transform duration-200 ease-in-out
                   lg:translate-x-0 lg:static lg:inset-0
                   ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header sidebar */}
          <div className="flex items-center justify-between h-16 px-6 bg-sidebar-hover border-b border-sidebar-border">
            <Link to="/espace-salarie" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-primary flex items-center justify-center">
                <User className="w-4 h-4 text-content-inverted" />
              </div>
              <span className="text-lg font-bold text-white">Espace Salarié</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-sidebar-text hover:text-white transition-colors"
              aria-label="Fermer la sidebar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            <p className="px-3 mb-2 text-xs font-semibold text-sidebar-text/60 uppercase tracking-wider">
              MON ESPACE
            </p>
            {navItems.map((item) => {
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                            ${active
                              ? 'bg-sidebar-active text-white shadow-sm'
                              : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white'
                            }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                  {active && <ChevronRight className="w-4 h-4 ml-auto opacity-70" />}
                </Link>
              );
            })}
          </nav>

          {/* User info + déconnexion */}
          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-content-inverted text-sm font-semibold flex-shrink-0">
                {(profil?.nom?.[0] ?? user?.prenom?.[0] ?? '?')}{(profil?.prenoms?.[0] ?? user?.nom?.[0] ?? '')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{nomEmploye || user?.email}</p>
                <p className="text-xs text-sidebar-text truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sidebar-text hover:text-white
                       hover:bg-sidebar-hover rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-surface border-b border-border flex items-center px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-content-muted hover:text-content transition-colors"
            aria-label="Ouvrir la sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 text-sm text-content-muted">
            <span className="text-content-subtle">Espace Salarié</span>
            <ChevronRight className="w-4 h-4" />
            <span className="font-medium text-content">{nomEmploye || 'Mon compte'}</span>
          </div>
        </header>

        {/* Contenu page */}
        <main className="flex-1 p-6 overflow-auto bg-surface-raised">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default EspaceSalarieLayout;
